<template>
    <modal name="extra-debt-payments-history" :width="900" height="auto" :adaptive="true" :scrollable="true"
        :click-to-close="!loading" @closed="resetModal">
        <div class="modal-form payments-history-modal">

            <div class="modal-form-header">
                <div class="d-flex align-items-center min-width-0">
                    <span class="modal-title-icon">
                        <i class="fa-solid fa-clock-rotate-left"></i>
                    </span>

                    <div class="min-width-0">
                        <h4 class="modal-title mb-0">
                            Historial de pagos
                        </h4>

                        <small class="modal-subtitle d-block text-truncate">
                            Consulta pagos parciales o el pago total del adeudo.
                        </small>
                    </div>
                </div>

                <button type="button" class="close ml-3" :disabled="loading" @click="closeModal">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            <div class="modal-form-body">

                <div v-if="loading" class="modal-loading">
                    <span class="loading-icon">
                        <i class="fa-solid fa-circle-notch fa-spin"></i>
                    </span>

                    <strong>Consultando pagos</strong>
                    <small class="text-muted">Espera un momento.</small>
                </div>

                <template v-else>

                    <div v-if="errorMessage" class="alert alert-danger history-alert">
                        <i class="fa-solid fa-circle-exclamation mr-1"></i>
                        {{ errorMessage }}
                    </div>

                    <section class="debt-context">
                        <div class="debt-context-row">

                            <div class="debt-identification">
                                <span class="debt-id">
                                    #{{ debt ? debt.id : '-' }}
                                </span>

                                <span class="source-badge" :class="debtType === 'contract'
                                    ? 'source-badge--contract'
                                    : 'source-badge--without'
                                    ">
                                    <i class="mr-1" :class="debtType === 'contract'
                                        ? 'fa-solid fa-file-contract'
                                        : 'fa-regular fa-file-lines'
                                        "></i>

                                    {{
                                        debtType === 'contract'
                                            ? 'Con contrato'
                                            : 'Sin contrato'
                                    }}
                                </span>
                            </div>

                            <span class="status-badge" :class="statusBadgeClass">
                                <i class="mr-1" :class="statusIcon"></i>

                                {{ statusText }}
                            </span>

                        </div>

                        <div class="debt-meta">

                            <div v-if="propertyName" class="meta-item">
                                <i class="fa-regular fa-building"></i>

                                <div>
                                    <small>Propiedad</small>
                                    <strong>{{ propertyName }}</strong>
                                </div>
                            </div>

                            <div v-if="clientName" class="meta-item">
                                <i class="fa-regular fa-user"></i>

                                <div>
                                    <small>Cliente</small>
                                    <strong>{{ clientName }}</strong>
                                </div>
                            </div>

                            <div class="meta-item">
                                <i class="fa-regular fa-calendar"></i>

                                <div>
                                    <small>Fecha del adeudo</small>
                                    <strong>{{ debtDate }}</strong>
                                </div>
                            </div>

                        </div>
                    </section>

                    <section class="summary-grid">

                        <div class="summary-card summary-total">
                            <span class="summary-icon">
                                <i class="fa-solid fa-file-invoice-dollar"></i>
                            </span>

                            <div>
                                <small>Total adeudo</small>
                                <strong>{{ money(summary.total) }}</strong>
                            </div>
                        </div>

                        <div class="summary-card summary-paid">
                            <span class="summary-icon">
                                <i class="fa-solid fa-circle-check"></i>
                            </span>

                            <div>
                                <small>Pagado</small>
                                <strong>{{ money(summary.paid) }}</strong>
                            </div>
                        </div>

                        <div class="summary-card summary-pending">
                            <span class="summary-icon">
                                <i class="fa-solid fa-hourglass-half"></i>
                            </span>

                            <div>
                                <small>Saldo pendiente</small>
                                <strong>{{ money(summary.pending) }}</strong>
                            </div>
                        </div>

                        <div class="summary-card summary-count">
                            <span class="summary-icon">
                                <i class="fa-solid fa-receipt"></i>
                            </span>

                            <div>
                                <small>Pagos</small>
                                <strong>{{ activePayments.length }}</strong>
                            </div>
                        </div>

                    </section>

                    <div class="progress-panel">

                        <div class="progress-header">
                            <span>Avance del pago</span>
                            <strong>{{ normalizedProgress }}%</strong>
                        </div>

                        <div class="payment-progress">
                            <div class="payment-progress-bar" :class="progressClass" :style="{
                                width: normalizedProgress + '%'
                            }"></div>
                        </div>

                    </div>

                    <section class="history-section">

                        <div class="history-section-header">

                            <div>
                                <h5>Movimientos registrados</h5>

                                <small>
                                    Cada fila corresponde a un pago aplicado al adeudo.
                                </small>
                            </div>

                            <button v-if="canRegisterPayment" type="button" class="btn btn-sm btn-outline-success"
                                @click="openPaymentForm">
                                <i class="fa-solid fa-plus mr-1"></i>
                                Registrar otro pago
                            </button>

                        </div>

                        <div class="table-container">

                            <div class="table-responsive">

                                <table class="
                                    table
                                    table-bordered
                                    table-hover
                                    text-center
                                    text-sm
                                    history-table
                                ">

                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>FECHA</th>
                                            <th class="text-left">DETALLE</th>
                                            <th class="text-right">PAGO</th>
                                            <th class="text-right">ACUMULADO</th>
                                            <th class="text-right">SALDO</th>
                                            <th>ESTATUS</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        <tr v-if="!payments.length">
                                            <td colspan="7" class="empty-cell">
                                                <div class="empty-state">
                                                    <span class="empty-icon">
                                                        <i class="fa-regular fa-folder-open"></i>
                                                    </span>

                                                    <strong>
                                                        Sin pagos registrados
                                                    </strong>

                                                    <small>
                                                        Este adeudo todavía no tiene movimientos.
                                                    </small>
                                                </div>
                                            </td>
                                        </tr>

                                        <tr v-for="(payment, index) in payments" :key="payment.check_id ||
                                            payment.pay_id ||
                                            index
                                            " :class="getPaymentRowClass(payment)">

                                            <td>
                                                <strong class="payment-number">
                                                    #{{ index + 1 }}
                                                </strong>

                                                <small class="d-block text-muted">
                                                    Check #{{ payment.check_id }}
                                                </small>
                                            </td>

                                            <td>
                                                <span class="payment-date">
                                                    {{ formatDate(payment.date) }}
                                                </span>
                                            </td>

                                            <td class="text-left">
                                                <div class="payment-detail">
                                                    <strong v-if="payment.description" class="payment-description">
                                                        {{ payment.description }}
                                                    </strong>

                                                    <span v-else class="text-muted">
                                                        Pago aplicado
                                                    </span>

                                                    <small v-if="payment.reference" class="payment-reference">
                                                        <i class="fa-solid fa-hashtag mr-1"></i>
                                                        {{ payment.reference }}
                                                    </small>

                                                    <small v-if="payment.pay_id" class="payment-pay-id">
                                                        Pay #{{ payment.pay_id }}
                                                    </small>
                                                </div>
                                            </td>

                                            <td class="text-right">
                                                <strong class="payment-amount">
                                                    {{ money(payment.amount) }}
                                                </strong>
                                            </td>

                                            <td class="text-right">
                                                <strong>
                                                    {{
                                                        payment.status === 'cancelled'
                                                            ? '—'
                                                            : money(payment.accumulated_paid)
                                                    }}
                                                </strong>
                                            </td>

                                            <td class="text-right">
                                                <strong v-if="
                                                    payment.remaining_balance !== null &&
                                                    payment.remaining_balance !== undefined
                                                " :class="{
                                                    'text-success':
                                                        Number(payment.remaining_balance) <= 0
                                                }">
                                                    {{ money(payment.remaining_balance) }}
                                                </strong>

                                                <span v-else class="text-muted">
                                                    —
                                                </span>
                                            </td>

                                            <td>
                                                <span class="movement-badge" :class="payment.status === 'cancelled'
                                                    ? 'movement-badge--cancelled'
                                                    : 'movement-badge--applied'
                                                    ">
                                                    <i class="mr-1" :class="payment.status === 'cancelled'
                                                        ? 'fa-solid fa-ban'
                                                        : 'fa-solid fa-check'
                                                        "></i>

                                                    {{
                                                        payment.status === 'cancelled'
                                                            ? 'Cancelado'
                                                            : 'Aplicado'
                                                    }}
                                                </span>

                                                <small v-if="
                                                    payment.status === 'cancelled' &&
                                                    payment.cancellation_message
                                                " class="cancellation-note" :title="payment.cancellation_message">
                                                    {{ payment.cancellation_message }}
                                                </small>
                                            </td>

                                        </tr>

                                    </tbody>
                                </table>

                            </div>
                        </div>
                    </section>

                </template>

            </div>

            <div class="modal-form-footer">

                <div class="footer-summary">
                    <span>
                        <strong>{{ activePayments.length }}</strong>
                        pagos activos
                    </span>

                    <span class="footer-separator">·</span>

                    <span>
                        Pagado:
                        <strong class="text-success">
                            {{ money(summary.paid) }}
                        </strong>
                    </span>
                </div>

                <div class="footer-actions">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeModal">
                        Cerrar
                    </button>

                    <button v-if="canRegisterPayment" type="button" class="btn btn-sm btn-success"
                        @click="openPaymentForm">
                        <i class="fa-solid fa-wallet mr-1"></i>
                        Registrar pago
                    </button>
                </div>

            </div>

        </div>
    </modal>
</template>


<script>

import request
    from '@/services/request';



const EMPTY_SUMMARY = () => ({
    total: 0,
    paid: 0,
    pending: 0,
    progress: 0,
    status: 'pending',
    is_paid: false,
});


export default {

    name:
        'ModalExtraDebtPaymentsHistory',
  

    data() {

        return {

            debt:
                null,

            debtType:
                'without_contract',

            summary:
                EMPTY_SUMMARY(),

            payments:
                [],

            loading:
                false,

            errorMessage:
                '',

        };

    },


    computed: {

        activePayments() {

            return this.payments.filter(
                payment =>
                    payment.status !== 'cancelled'
            );

        },


        normalizedProgress() {

            return Math.max(
                0,
                Math.min(
                    100,
                    Number(
                        this.summary.progress || 0
                    )
                )
            );

        },


        isPaid() {

            return (
                this.summary.is_paid === true ||
                Number(
                    this.summary.pending || 0
                ) <= 0
            );

        },


        canRegisterPayment() {

            return (
                this.debt &&
                !this.isPaid &&
                Number(
                    this.summary.pending || 0
                ) > 0
            );

        },


        statusText() {

            if (this.isPaid) {
                return 'Pagado';
            }

            if (
                Number(
                    this.summary.paid || 0
                ) > 0
            ) {
                return 'Pago parcial';
            }

            return 'Pendiente';

        },


        statusBadgeClass() {

            if (this.isPaid) {
                return 'status-badge--paid';
            }

            if (
                Number(
                    this.summary.paid || 0
                ) > 0
            ) {
                return 'status-badge--partial';
            }

            return 'status-badge--pending';

        },


        statusIcon() {

            if (this.isPaid) {
                return 'fa-solid fa-check';
            }

            if (
                Number(
                    this.summary.paid || 0
                ) > 0
            ) {
                return 'fa-solid fa-layer-group';
            }

            return 'fa-solid fa-clock';

        },


        progressClass() {

            if (this.isPaid) {
                return 'payment-progress-bar--paid';
            }

            if (
                Number(
                    this.summary.paid || 0
                ) > 0
            ) {
                return 'payment-progress-bar--partial';
            }

            return 'payment-progress-bar--pending';

        },


        propertyName() {

            if (
                !this.debt ||
                !this.debt.contract ||
                !this.debt.contract.building
            ) {
                return '';
            }

            return this.debt
                .contract
                .building
                .name || '';

        },


        clientName() {

            if (!this.debt) {
                return '';
            }

            const user =
                this.debt.user ||
                (
                    this.debt.contract
                        ? this.debt.contract.user
                        : null
                );

            if (!user) {
                return '';
            }

            return [
                user.name,
                user.last_name,
                user.second_last_name,
            ]
                .filter(Boolean)
                .join(' ');

        },


        debtDate() {

            if (
                !this.debt ||
                !this.debt.due_date
            ) {
                return 'Sin fecha';
            }

            return this.formatDate(
                this.debt.due_date
            );

        },

    },


    methods: {

        async openModal(buy) {

            this.resetModal();

            this.debt =
                buy;

            this.debtType =
                this.resolveDebtType(
                    buy
                );

            this.$modal.show(
                'extra-debt-payments-history'
            );

            await this.loadPayments();

        },


        resolveDebtType(buy) {

            if (
                buy &&
                (
                    buy.source_type === 'contract' ||
                    buy.has_contract === true ||
                    buy.contract_id ||
                    buy.contract
                )
            ) {
                return 'contract';
            }

            return 'without_contract';

        },


        async loadPayments() {

            if (
                !this.debt ||
                !this.debt.id
            ) {
                return;
            }

            this.loading =
                true;

            this.errorMessage =
                '';

            try {

                const response =
                    await request.get(
                        `/extra-debts/${this.debtType}/${this.debt.id}/payments`
                    );

                const data =
                    response &&
                        response.data
                        ? response.data
                        : response;

                this.summary = {
                    ...EMPTY_SUMMARY(),
                    ...(data.summary || {}),
                };

                this.payments =
                    Array.isArray(
                        data.payments
                    )
                        ? data.payments
                        : [];

            } catch (error) {

                this.errorMessage =
                    this.getErrorMessage(
                        error
                    );

            } finally {

                this.loading =
                    false;

            }

        },


        openPaymentForm() {

            if (
                !this.debt ||
                !this.canRegisterPayment
            ) {
                return;
            }

            const debt =
                this.debt;

            this.$modal.hide(
                'extra-debt-payments-history'
            );

           
            this.$nextTick(() => {
                Event.$emit(
                    'event-open-extra-debt-payment-form',
                    debt
                );
            });

        },


        getPaymentRowClass(payment) {

            return {
                'row-cancelled':
                    payment.status === 'cancelled',

                'row-applied':
                    payment.status !== 'cancelled',
            };

        },


        getErrorMessage(error) {

            if (
                error &&
                error.response &&
                error.response.data
            ) {

                return (
                    error.response.data.message ||
                    error.response.data.error ||
                    'No fue posible consultar el historial de pagos.'
                );

            }

            return 'No fue posible consultar el historial de pagos.';

        },


        formatDate(value) {

            if (!value) {
                return 'Sin fecha';
            }

            const date =
                this.moment(
                    value
                );

            return date.isValid()
                ? date.format(
                    'DD/MM/YYYY'
                )
                : value;

        },


        money(value) {

            return new Intl.NumberFormat(
                'es-MX',
                {
                    style:
                        'currency',

                    currency:
                        'MXN',

                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2,
                }
            ).format(
                Number(
                    value || 0
                )
            );

        },


        closeModal() {

            if (this.loading) {
                return;
            }

            this.$modal.hide(
                'extra-debt-payments-history'
            );

        },


        resetModal() {

            this.debt =
                null;

            this.debtType =
                'without_contract';

            this.summary =
                EMPTY_SUMMARY();

            this.payments =
                [];

            this.loading =
                false;

            this.errorMessage =
                '';

        },

    },


    created() {

        Event.$on(
            'event-open-extra-debt-payments-history',
            this.openModal
        );

    },


    beforeDestroy() {

        Event.$off(
            'event-open-extra-debt-payments-history',
            this.openModal
        );

    },

};

</script>


<style scoped>
.payments-history-modal {
    background-color: #ffffff;
}

.min-width-0 {
    min-width: 0;
}

.modal-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background-color: #ffffff;
    border-bottom: 1px solid #e5e7eb;
}

.modal-title-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 11px;
    color: #596780;
    background-color: #f1f3f5;
    border-radius: 8px;
}

.modal-title {
    color: #343a40;
    font-size: 17px;
    font-weight: 600;
}

.modal-subtitle {
    margin-top: 2px;
    color: #7b8490;
    font-size: 11px;
}

.modal-form-body {
    max-height: 74vh;
    overflow-y: auto;
    padding: 18px 20px;
}

.modal-form-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 13px 20px;
    background-color: #f8f9fa;
    border-top: 1px solid #e5e7eb;
}

.modal-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    min-height: 280px;
    color: #59636e;
}

.loading-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-bottom: 8px;
    color: #596780;
    background-color: #f1f3f5;
    border-radius: 50%;
    font-size: 19px;
}

.history-alert {
    margin-bottom: 14px;
    font-size: 12px;
}

.debt-context {
    padding: 13px 14px;
    margin-bottom: 13px;
    background-color: #f8f9fa;
    border: 1px solid #e3e6e9;
    border-radius: 8px;
}

.debt-context-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 9px;
    margin-bottom: 9px;
    border-bottom: 1px solid #e3e6e9;
}

.debt-identification {
    display: flex;
    align-items: center;
    gap: 7px;
}

.debt-id {
    color: #343a40;
    font-size: 16px;
    font-weight: 700;
}

.source-badge,
.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 600;
    white-space: nowrap;
}

.source-badge--contract {
    color: #1d4e89;
    background-color: #e9f0ff;
}

.source-badge--without {
    color: #59636e;
    background-color: #e9ecef;
}

.status-badge--pending {
    color: #856404;
    background-color: #fff3cd;
}

.status-badge--partial {
    color: #1d4e89;
    background-color: #dbeafe;
}

.status-badge--paid {
    color: #155724;
    background-color: #d4edda;
}

.debt-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 7px;
}

.meta-item>i {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: #7a8591;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
}

.meta-item small {
    display: block;
    color: #8a939c;
    font-size: 8px;
    text-transform: uppercase;
}

.meta-item strong {
    display: block;
    max-width: 190px;
    overflow: hidden;
    color: #495057;
    font-size: 10px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 9px;
}

.summary-card {
    display: flex;
    align-items: center;
    min-height: 68px;
    padding: 9px 10px;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-left: 4px solid #6c757d;
    border-radius: 8px;
}

.summary-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 31px;
    width: 31px;
    height: 31px;
    margin-right: 8px;
    color: #596780;
    background-color: #f1f3f5;
    border-radius: 50%;
}

.summary-card small {
    display: block;
    color: #8a939c;
    font-size: 7px;
    font-weight: 700;
    text-transform: uppercase;
}

.summary-card strong {
    display: block;
    color: #343a40;
    font-size: 12px;
    font-weight: 700;
}

.summary-total {
    border-left-color: #596780;
}

.summary-paid {
    border-left-color: #28a745;
}

.summary-paid strong {
    color: #218838;
}

.summary-pending {
    border-left-color: #ffc107;
}

.summary-pending strong {
    color: #856404;
}

.summary-count {
    border-left-color: #4b72d9;
}

.progress-panel {
    padding: 8px 10px;
    margin-bottom: 14px;
    background-color: #f8f9fa;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
}

.progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px;
    color: #6c757d;
    font-size: 9px;
    font-weight: 600;
}

.payment-progress {
    width: 100%;
    height: 7px;
    overflow: hidden;
    background-color: #e4e7eb;
    border-radius: 999px;
}

.payment-progress-bar {
    height: 100%;
    border-radius: 999px;
}

.payment-progress-bar--pending {
    background-color: #ffc107;
}

.payment-progress-bar--partial {
    background-color: #4b72d9;
}

.payment-progress-bar--paid {
    background-color: #28a745;
}

.history-section {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}

.history-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 13px;
    background-color: #ffffff;
    border-bottom: 1px solid #e5e7eb;
}

.history-section-header h5 {
    margin: 0;
    color: #495057;
    font-size: 12px;
    font-weight: 700;
}

.history-section-header small {
    display: block;
    margin-top: 2px;
    color: #8a939c;
    font-size: 9px;
}

.history-table {
    min-width: 820px;
    margin-bottom: 0;
}

.history-table thead th {
    padding: 8px 7px;
    color: #59636e;
    background-color: #f1f3f5;
    border-color: #dee2e6;
    font-size: 9px;
    font-weight: 700;
    vertical-align: middle;
    white-space: nowrap;
}

.history-table tbody td {
    padding: 8px 7px;
    color: #495057;
    border-color: #e7eaed;
    font-size: 10px;
    vertical-align: middle;
}

.history-table tbody tr:hover {
    background-color: #fafbfc;
}

.payment-number {
    color: #343a40;
    font-size: 11px;
}

.payment-date {
    color: #495057;
    font-weight: 600;
}

.payment-detail {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.payment-description {
    display: block;
    max-width: 260px;
    overflow: hidden;
    color: #343a40;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.payment-reference,
.payment-pay-id {
    margin-top: 2px;
    color: #8a939c;
    font-size: 8px;
}

.payment-amount {
    color: #218838;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.movement-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 7px;
    border-radius: 999px;
    font-size: 8px;
    font-weight: 600;
    white-space: nowrap;
}

.movement-badge--applied {
    color: #155724;
    background-color: #d4edda;
}

.movement-badge--cancelled {
    color: #721c24;
    background-color: #f8d7da;
}

.cancellation-note {
    display: block;
    max-width: 110px;
    overflow: hidden;
    margin: 3px auto 0;
    color: #9b5960;
    font-size: 8px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.row-cancelled {
    opacity: .68;
    background-color: #fafafa !important;
}

.row-cancelled td:first-child {
    border-left: 3px solid #dc3545 !important;
}

.row-applied td:first-child {
    border-left: 3px solid #28a745 !important;
}

.empty-cell {
    padding: 30px !important;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-bottom: 8px;
    color: #8a939c;
    background-color: #f1f3f5;
    border-radius: 50%;
    font-size: 17px;
}

.empty-state strong {
    color: #59636e;
    font-size: 11px;
}

.empty-state small {
    margin-top: 2px;
    color: #9aa2aa;
    font-size: 9px;
}

.footer-summary {
    display: flex;
    align-items: center;
    color: #7b8490;
    font-size: 9px;
}

.footer-separator {
    margin: 0 6px;
    color: #adb5bd;
}

.footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

@media (max-width: 767px) {

    .modal-form-header,
    .modal-form-body,
    .modal-form-footer {
        padding-left: 14px;
        padding-right: 14px;
    }

    .debt-context-row,
    .history-section-header,
    .modal-form-footer {
        align-items: flex-start;
        flex-direction: column;
    }

    .summary-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .footer-actions {
        width: 100%;
        justify-content: flex-end;
    }

}
</style>
